import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import postHealthOverview, {
  HealthOverviewInitResponse,
  HealthOverviewResponse,
} from "../http/getHealthOverview";
import { ReloginContext } from "../ReloginContext";
import { useAsyncOperation } from "./useAsyncOperation";
import { getAuthTokenBlocking } from "./utils";

const SITE_ID = "global";
const CACHE_KEY = ["healthOverview"];
const CACHE_TTL_MS = 60_000; // 1 minute

export const useHealthOverview = () => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();
  const [operationKey, setOperationKey] = useState<string | null>(null);

  const cachedQuery = useQuery<HealthOverviewResponse>(
    CACHE_KEY,
    () => Promise.reject("no data"),
    {
      enabled: false,
      staleTime: CACHE_TTL_MS,
      cacheTime: CACHE_TTL_MS,
    },
  );

  const hasFreshCache =
    cachedQuery.data !== undefined && !cachedQuery.isStale;

  const initMutation = useMutation<HealthOverviewInitResponse>(async () =>
    postHealthOverview(await getAuthTokenBlocking(getToken, relogin)),
  );

  const asyncOperation = useAsyncOperation({
    siteId: SITE_ID,
    operationKey,
  });

  useEffect(() => {
    if (hasFreshCache) return;
    if (!initMutation.isIdle) return;
    initMutation.mutate(undefined, {
      onSuccess: (response) => {
        setOperationKey(response.operationKey);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      asyncOperation.data?.status === "completed" &&
      asyncOperation.data.result
    ) {
      queryClient.setQueryData(
        CACHE_KEY,
        asyncOperation.data.result as unknown as HealthOverviewResponse,
      );
    }
  }, [asyncOperation.data?.status, asyncOperation.data?.result, queryClient]);

  const data = cachedQuery.data ?? undefined;

  const isLoading =
    !hasFreshCache &&
    (initMutation.isLoading ||
      asyncOperation.isPolling ||
      (asyncOperation.data?.status !== "completed" &&
        asyncOperation.data?.status !== "failed" &&
        !asyncOperation.timedOut &&
        !initMutation.isError));

  const isError =
    !hasFreshCache &&
    (initMutation.isError ||
      asyncOperation.data?.status === "failed" ||
      asyncOperation.timedOut);

  const progress = asyncOperation.data?.progress;

  return {
    data,
    isLoading,
    isError,
    progress,
  };
};
